import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";

// The break catalogue agents pick from. Order here is the order they see.
//
// A type's id is fixed once created, because attendance records store it; the
// label can change freely and past breaks keep the label they were taken under.

const SWATCHES = [
  "#d97706",
  "#0ea5e9",
  "#8b5cf6",
  "#0C237B",
  "#059669",
  "#14b8a6",
  "#dc2626",
  "#6b7280",
];

const EMPTY = { label: "", maxMinutes: "", color: SWATCHES[0], isActive: true };

export default function BreakTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.breakTypes, {
        params: { includeInactive: true },
      });
      setTypes(data?.data || []);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load break types");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, color: SWATCHES[types.length % SWATCHES.length] });
    setFormOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      label: t.label,
      maxMinutes: t.maxMinutes ? String(t.maxMinutes) : "",
      color: t.color,
      isActive: t.isActive,
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        label: form.label.trim(),
        maxMinutes: form.maxMinutes === "" ? 0 : Number(form.maxMinutes),
        color: form.color,
        isActive: form.isActive,
      };
      const { data } = editing
        ? await axiosInstance.put(`${apiEndPointsConfig.breakTypes}/${editing.id}`, payload)
        : await axiosInstance.post(apiEndPointsConfig.breakTypes, payload);
      setNotice(data?.message || "Saved");
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the break type");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.delete(
        `${apiEndPointsConfig.breakTypes}/${deleting.id}`
      );
      setNotice(data?.message || "Removed");
      setDeleting(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove the break type");
      setDeleting(null);
    } finally {
      setSaving(false);
    }
  };

  const move = async (index, delta) => {
    const next = [...types];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setTypes(next); // optimistic: the list is short and the call is tiny
    try {
      await axiosInstance.put(`${apiEndPointsConfig.breakTypes}/reorder`, {
        ids: next.map((t) => t.id),
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the new order");
      load();
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ fontWeight: 700, color: "#374151" }}>Break Types</Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            What agents can pick from when they start a break, in this order.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px" }}
        >
          Add Break Type
        </Button>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", overflow: "hidden" }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "#6b7280", bgcolor: "#f9fafb" } }}>
                  <TableCell width={90}>Order</TableCell>
                  <TableCell>Break Type</TableCell>
                  <TableCell>Stored As</TableCell>
                  <TableCell align="right">Allowed</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!types.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#9ca3af" }}>
                      No break types yet. Agents need at least one to log a break.
                    </TableCell>
                  </TableRow>
                )}
                {types.map((t, i) => (
                  <TableRow key={t.id} hover sx={{ opacity: t.isActive ? 1 : 0.55 }}>
                    <TableCell>
                      <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}>
                        <ArrowUp size={14} />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={i === types.length - 1}
                        onClick={() => move(i, 1)}
                      >
                        <ArrowDown size={14} />
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            bgcolor: t.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
                          {t.label}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Tooltip title="Attendance records store this id. It never changes, so renaming the label is safe.">
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "ui-monospace, Menlo, monospace", color: "#9ca3af" }}
                        >
                          {t.key}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right" sx={{ color: "#6b7280" }}>
                      {t.maxMinutes > 0 ? `${t.maxMinutes} min` : "No limit"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={t.isActive ? "Active" : "Retired"}
                        sx={{
                          bgcolor: t.isActive ? "#ecfdf5" : "#f3f4f6",
                          color: t.isActive ? "#059669" : "#6b7280",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(t)} sx={{ color: "#0C237B" }}>
                            <Pencil size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => setDeleting(t)} sx={{ color: "#dc2626" }}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Create / edit ─────────────────────────────────────────────────── */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={submit}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {editing ? "Edit Break Type" : "Add Break Type"}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  label="Label *"
                  placeholder="e.g. Team Huddle"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  helperText={
                    editing
                      ? `Stored as "${editing.key}" -- past breaks keep their original label`
                      : "What agents see in the break picker"
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Allowed minutes"
                  placeholder="0"
                  value={form.maxMinutes}
                  onChange={(e) => setForm((f) => ({ ...f, maxMinutes: e.target.value }))}
                  inputProps={{ min: 0, max: 600 }}
                  helperText="Leave blank or 0 for no limit. Overruns are flagged, never blocked."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1 }}>
                  Colour
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {SWATCHES.map((c) => (
                    <Box
                      key={c}
                      onClick={() => setForm((f) => ({ ...f, color: c }))}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: c,
                        cursor: "pointer",
                        outline: form.color === c ? "3px solid #c7d2fe" : "none",
                        outlineOffset: 2,
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    />
                  }
                  label="Available to agents"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setFormOpen(false)} sx={{ color: "#6b7280" }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !form.label.trim()}
              sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px" }}
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Remove ────────────────────────────────────────────────────────── */}
      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove break type?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Agents will no longer be able to pick <strong>{deleting?.label}</strong>. If it has ever
            been used, it is retired rather than deleted so past breaks keep their label.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleting(null)} sx={{ color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={confirmDelete}
            sx={{ borderRadius: "8px" }}
          >
            {saving ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
